// Itens de combate/loja + loot importado do Poke Idle (tools/import-pokeidle-loot.mjs).
import {
  budgetedQuantity,
  DROP_SELL_VALUE_MULTIPLIER,
  dropBudgetForLevel,
  lootChanceToProbability,
  primaryDropChanceForLevel,
  scaledLootQuantity,
  signatureDropChanceForStage,
  speciesLootQtyScale,
  stoneDropChanceForLevel,
} from './economy-balance.mjs';
import { IMPORTED_LOOT, IMPORTED_STONES } from './generated/loot-items.js';
import { speciesLootTable } from './generated/species-loot.js';
import { DROP_ONLY_SLUGS, isDropOnlySlug } from './drop-only-items.mjs';
import { rollEndgameDrops } from './endgame-loot.mjs';

export { DROP_ONLY_SLUGS, canonicalizeDropOnlyMap } from './drop-only-items.mjs';

export {
  DROP_SELL_VALUE_MULTIPLIER,
  dropBudgetForLevel,
  primaryDropChanceForLevel,
  signatureDropChanceForStage,
  stoneDropChanceForLevel,
} from './economy-balance.mjs';

const img = (slug) => `public/assets/items/${slug}.png`;

export const BALLS = [
  { slug: 'pokeball', name: 'Pokébola', capture: 1, price: 40, desc: '1x de chance de captura.', img: img('pokeball'), kind: 'ball' },
  { slug: 'greatball', name: 'Greatball', capture: 2, price: 100, desc: '2x de chance de captura.', img: img('greatball'), kind: 'ball' },
  { slug: 'superball', name: 'Superball', capture: 3, price: 250, desc: '3x de chance de captura.', img: img('superball'), kind: 'ball' },
  { slug: 'ultraball', name: 'Ultraball', capture: 4, price: 600, desc: '4x de chance de captura.', img: img('ultraball'), kind: 'ball' },
  { slug: 'apexball', name: 'Apex Ball', capture: 8, price: null, tradeable: false, autoRestock: false, premium: true, desc: '~25% nos especiais de zona Threat 3/4.', img: img('ultraball'), zoneSpecialChance: 0.25, kind: 'ball' },
  { slug: 'masterball', name: 'Masterball', capture: 100, price: null, tradeable: false, autoRestock: false, premium: true, desc: '100x de chance. Sempre captura.', img: img('masterball'), kind: 'ball' },
];

/** Chance fixa da Apex Ball vs lendário/mítico de zona. */
export const APEX_BALL_ZONE_SPECIAL_CHANCE = 0.25;

export const POTIONS = [
  { slug: 'potion', name: 'Poção', heal: 0.3, price: 40, desc: 'Restaura 30% da vida de um pokémon.', img: img('potion'), kind: 'potion' },
  { slug: 'great-potion', name: 'Super Poção', heal: 0.6, price: 100, desc: 'Restaura 60% da vida de um pokémon.', img: img('great-potion'), kind: 'potion' },
  { slug: 'ultra-potion', name: 'Ultra Poção', heal: 0.8, price: 220, desc: 'Restaura 80% da vida de um pokémon.', img: img('ultra-potion'), kind: 'potion' },
  { slug: 'hyper-potion', name: 'Hyper Poção', heal: 0.9, price: 500, desc: 'Restaura 90% da vida de um pokémon.', img: img('hyper-potion'), kind: 'potion' },
  { slug: 'full-restore', name: 'Restauração Total', heal: 1, price: 1000, desc: 'Restaura toda a vida de um pokémon.', img: img('full-restore'), kind: 'potion' },
  { slug: 'medicine', name: 'Antídoto', cure: 'poison', price: 80, desc: 'Cura o envenenamento de um pokémon.', img: img('medicine'), kind: 'potion' },
  { slug: 'revive', name: 'Revive', revive: true, price: 300, desc: 'Revive um pokémon desmaiado.', img: img('revive'), kind: 'potion' },
];

const BASE_STONES = [
  { slug: 'leaf-stone', name: 'Pedra Folha', price: 5000, desc: 'Evolui pokémons de planta.', type: 'planta', img: img('leaf-stone') },
  { slug: 'water-stone', name: 'Pedra Água', price: 5000, desc: 'Evolui pokémons de água.', type: 'água', img: img('water-stone') },
  { slug: 'thunder-stone', name: 'Pedra Trovão', price: 5000, desc: 'Evolui pokémons elétricos.', type: 'elétrico', img: img('thunder-stone') },
  { slug: 'fire-stone', name: 'Pedra Fogo', price: 5000, desc: 'Evolui pokémons de fogo.', type: 'fogo', img: img('fire-stone') },
  { slug: 'sun-stone', name: 'Pedra Solar', price: 5000, desc: 'Evolui pokémons que amam sol.', type: 'planta', img: img('sun-stone') },
  { slug: 'moon-stone', name: 'Pedra Lunar', price: 5000, desc: 'Referência à Lua. Evolui alguns pokémons.', type: 'fada', img: img('moon-stone') },
  { slug: 'ice-stone', name: 'Pedra Gelo', price: 5000, desc: 'Evolui pokémons de gelo.', type: 'gelo', img: img('ice-stone') },
  { slug: 'shiny-stone', name: 'Pedra Shiny', price: 8000, desc: 'Evolui pokémons brilhantes.', type: 'aço', img: img('shiny-stone') },
  { slug: 'venom-stone', name: 'Pedra Veneno', price: 5000, desc: 'Evolui pokémons venenosos.', type: 'veneno', img: img('venom-stone') },
  { slug: 'rock-stone', name: 'Pedra Pedra', price: 5000, desc: 'Evolui pokémons de pedra.', type: 'pedra', img: img('rock-stone') },
  { slug: 'punch-stone', name: 'Pedra Soco', price: 5000, desc: 'Evolui pokémons lutadores.', type: 'lutador', img: img('punch-stone') },
  { slug: 'cocoon-stone', name: 'Pedra Casulo', price: 5000, desc: 'Evolui pokémons casulo.', type: 'inseto', img: img('cocoon-stone') },
  { slug: 'crystal-stone', name: 'Pedra Cristal', price: 5000, desc: 'Evolui pokémons de cristal.', type: 'pedra', img: img('crystal-stone') },
  { slug: 'darkness-stone', name: 'Pedra Sombria', price: 5000, desc: 'Evolui pokémons sombrios.', type: 'sombrio', img: img('darkness-stone') },
  { slug: 'earth-stone', name: 'Pedra Terra', price: 5000, desc: 'Evolui pokémons de terra.', type: 'terra', img: img('earth-stone') },
  { slug: 'enigma-stone', name: 'Pedra Enigma', price: 5000, desc: 'Evolui pokémons psíquicos.', type: 'psíquico', img: img('enigma-stone') },
  { slug: 'heart-stone', name: 'Pedra Coração', price: 5000, desc: 'Evolui pokémons de fada.', type: 'fada', img: img('heart-stone') },
];

function mergeStones() {
  const bySlug = new Map(BASE_STONES.map((stone) => [stone.slug, { ...stone, kind: 'stone' }]));
  for (const imported of IMPORTED_STONES) {
    const current = bySlug.get(imported.slug);
    if (current) {
      bySlug.set(imported.slug, {
        ...current,
        kind: 'stone',
        img: imported.img || current.img,
        name: current.name,
        price: current.price ?? imported.price,
        type: current.type ?? imported.type ?? null,
        desc: current.desc || imported.desc,
      });
    } else {
      bySlug.set(imported.slug, {
        slug: imported.slug,
        name: imported.name,
        price: imported.price || 5000,
        desc: imported.desc || `Pedra ${imported.name}.`,
        type: imported.type || null,
        kind: 'stone',
        img: imported.img || img(imported.slug),
      });
    }
  }
  return [...bySlug.values()];
}

export const STONES = mergeStones();

/** Materiais de loot (tabelas por espécie). Inclui os 18 drops legado + catálogo importado. */
export const DROPS = IMPORTED_LOOT.filter((item) => !isDropOnlySlug(item.slug)).map((item) => ({
  slug: item.slug,
  name: item.name,
  sell: item.sell,
  rare: !!item.rare,
  img: item.img,
  type: null,
  kind: 'loot',
}));
const DROP_SLUGS = new Set(DROPS.map((item) => item.slug));

export const FISHING_ROD_ITEMS = [
  { slug: 'old-rod', name: 'Vara Velha', price: 80, desc: 'Pesca espécies aquáticas básicas.', img: 'public/assets/items/old-rod.svg', fishing: true, kind: 'tool' },
  { slug: 'good-rod', name: 'Boa Vara', price: 450, desc: 'Pesca aquáticos até faixas médias.', img: 'public/assets/items/good-rod.svg', fishing: true, kind: 'tool' },
  { slug: 'super-rod', name: 'Super Vara', price: 2200, desc: 'Pesca aquáticos em qualquer faixa.', img: 'public/assets/items/super-rod.svg', fishing: true, kind: 'tool' },
];

/** Itens de progressão dos blocos Shiny/Breeding. */
export const SPECIAL_ITEMS = Object.freeze([
  Object.freeze({
    slug: 'shiny-card', name: 'Shiny Card', sell: 500, rare: true,
    desc: 'Carta genérica usada para invocar uma batalha shiny no Altar.',
    img: 'public/assets/items/rare-pokemon-picture.png', shinyCard: true,
  }),
  Object.freeze({
    slug: 'pheromone', name: 'Feromônio', sell: 500, rare: true,
    desc: 'Melhora a rolagem de qualidade no Centro de Breeding.',
    img: 'public/assets/items/strange-pheromone.png', pheromone: true, consumable: true, dropOnly: true,
  }),
]);

/** Todas as TMs são consumíveis, AoE e exigem nível 100 para equipar. */
export const TM_ITEMS = Object.freeze([
  ['electric', 'TM Campo de Descarga', 'elétrico', 'aoe-electric'],
  ['fire', 'TM Onda de Inferno', 'fogo', 'aoe-fire'],
  ['water', 'TM Onda de Maré', 'água', 'aoe-water'],
  ['grass', 'TM Tempestade de Pétalas', 'planta', 'aoe-grass'],
  ['bug', 'TM Ataque de Enxame', 'inseto', 'aoe-bug'],
  ['flying', 'TM Furacão', 'voador', 'aoe-flying'],
  ['normal', 'TM Hiper Voz', 'normal', 'aoe-normal'],
  ['psychic', 'TM Onda Psíquica', 'psíquico', 'aoe-psychic'],
  ['rock', 'TM Deslizamento de Rochas', 'pedra', 'aoe-rock'],
  ['ground', 'TM Terremoto', 'terra', 'aoe-ground'],
  ['ghost', 'TM Vento Sinistro', 'fantasma', 'aoe-ghost'],
  ['dragon', 'TM Meteoro Draco', 'dragão', 'aoe-dragon'],
  ['fighting', 'TM Onda de Vácuo', 'lutador', 'aoe-fighting'],
  ['poison', 'TM Onda de Lodo', 'veneno', 'aoe-poison'],
  ['dark', 'TM Rosnado Sombrio', 'sombrio', 'aoe-dark'],
  ['steel', 'TM Tempestade de Aço', 'aço', 'aoe-steel'],
  ['ice', 'TM Nevasca', 'gelo', 'aoe-ice'],
  ['fairy', 'TM Brilho Mágico', 'fada', 'aoe-fairy'],
].map(([slug, name, type, move]) => Object.freeze({
  slug: `tm-${slug}`,
  name,
  type,
  move,
  sell: 2400,
  desc: `Ensina ${name.replace(/^TM /, '')}, um golpe em área.`,
  img: 'public/assets/items/tm-disk-piece.png',
  tm: true,
  aoe: true,
  consumable: true,
  minLevel: 100,
  dropOnly: true,
})));

export const DROP_ONLY_ITEMS = Object.freeze([
  ...SPECIAL_ITEMS.filter((item) => isDropOnlySlug(item.slug)),
  ...TM_ITEMS.filter((item) => isDropOnlySlug(item.slug)),
]);

/** Itens que aparecem no bucket de drops sem duplicar os materiais importados. */
export const DROP_ITEMS = Object.freeze([...DROPS, ...DROP_ONLY_ITEMS]);

/** O catálogo de TM continua disponível para equipamento/Test Lab, mas não para compra. */
export const PURCHASABLE_TM_ITEMS = Object.freeze(TM_ITEMS.filter((item) => !item.dropOnly));

export const ITEM_BY_SLUG = Object.fromEntries(
  [...BALLS, ...POTIONS, ...STONES, ...DROPS, ...FISHING_ROD_ITEMS, ...SPECIAL_ITEMS, ...TM_ITEMS].map((item) => [item.slug, item]),
);

/** Loot/pedra vão para state.drops; resto para state.inventory. */
export function isDropBucketSlug(slug){
  if(isDropOnlySlug(slug))return true;
  const kind=ITEM_BY_SLUG[slug]?.kind;
  if(kind==='loot'||kind==='stone')return true;
  if(DROP_SLUGS.has(slug))return true;
  return STONES.some(stone=>stone.slug===slug);
}

/** Drop “representativo” por tipo — usado só no fallback / UI legada. */
const TYPE_MATERIAL_SLUGS = {
  elétrico: 'drop-electric-box',
  fogo: 'drop-essence-fire',
  água: 'drop-water-gem',
  planta: 'drop-seed',
  inseto: 'drop-bug-antenna',
  voador: 'drop-feather',
  normal: 'drop-tooth',
  psíquico: 'drop-twisted-spoon',
  pedra: 'drop-stone-orb',
  terra: 'drop-bone',
  fantasma: 'drop-ghost-trace',
  dragão: 'drop-dragon-tooth',
  lutador: 'drop-champion-belt',
  veneno: 'drop-poison-bottle',
  sombrio: 'drop-fox-tail',
  aço: 'drop-iron-pin',
  gelo: 'drop-ice-orb',
  fada: 'drop-luck-medal',
};

export const DROP_BY_TYPE = Object.fromEntries(
  Object.entries(TYPE_MATERIAL_SLUGS)
    .map(([type, slug]) => [type, ITEM_BY_SLUG[slug]])
    .filter(([, item]) => item),
);

export function normalizeSpeciesSlug(speciesName = '') {
  return String(speciesName || '')
    .toLowerCase()
    .replace(/♀/g, '-f')
    .replace(/♂/g, '-m')
    .replace(/_/g, '-')
    .replace(/[''`]/g, '')
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function signatureDropForSpecies(types = [], speciesName = '') {
  const table = speciesLootTable(normalizeSpeciesSlug(speciesName));
  if (table?.length) {
    const mid = table.find((row) => !row.stone && row.chance > 0 && row.chance < 40000);
    if (mid && ITEM_BY_SLUG[mid.slug]) return ITEM_BY_SLUG[mid.slug];
    const loot = table.find((row) => !row.stone && ITEM_BY_SLUG[row.slug]);
    if (loot) return ITEM_BY_SLUG[loot.slug];
  }
  const signaturePool = types.map((type) => DROP_BY_TYPE[type]).filter(Boolean);
  if (!signaturePool.length) return null;
  const hash = [...String(speciesName)].reduce((sum, char) => (sum * 31 + char.charCodeAt(0)) >>> 0, 7);
  return signaturePool[hash % signaturePool.length];
}

export const STONES_BY_TYPE = STONES.reduce((map, stone) => {
  if (!stone.type) return map;
  (map[stone.type] = map[stone.type] || []).push(stone);
  return map;
}, {});

export const sellValue = (item) => {
  if (!item) return 0;
  if (item.sell != null) {
    const isPokemonDrop = DROP_SLUGS.has(item.slug);
    return Math.round(item.sell * (isPokemonDrop ? DROP_SELL_VALUE_MULTIPLIER : 1));
  }
  if (STONES.some((stone) => stone.slug === item.slug)) return Math.round((item.price || 0) * 0.2);
  return item.price || 0;
};

function pushDrop(results, slug, qty, flags = {}) {
  if (!slug || qty <= 0 || !ITEM_BY_SLUG[slug]) return;
  const existing = results.find((drop) => drop.slug === slug);
  if (existing) existing.qty += qty;
  else results.push({ slug, name: ITEM_BY_SLUG[slug].name, qty, ...flags });
}

function stableExpectedValue(table, level) {
  let total = 0;
  for (const row of table) {
    if (row.stone) continue;
    const item = ITEM_BY_SLUG[row.slug];
    if (!item) continue;
    const p = lootChanceToProbability(row.chance);
    const avgQty = (Math.max(1, row.min) + Math.max(row.min, row.max)) / 2;
    const factor = Math.min(1.35, 0.4 + Math.max(1, level) / 180);
    total += p * avgQty * factor * sellValue(item);
  }
  return total;
}

function rollSpeciesTable(table, level, stage, random) {
  const budget = dropBudgetForLevel(level, stage);
  const scale = speciesLootQtyScale(stableExpectedValue(table, level), budget);
  const results = [];
  for (const row of table) {
    if (random() >= lootChanceToProbability(row.chance)) continue;
    const item = ITEM_BY_SLUG[row.slug];
    if (!item) continue;
    if (row.stone) {
      pushDrop(results, row.slug, 1, { rare: true });
      continue;
    }
    let qty = scaledLootQuantity(row.min, row.max, level, random);
    qty = budgetedQuantity(1, qty * scale, random);
    if (qty > 0) pushDrop(results, row.slug, qty, row.chance < 20000 ? { signature: true } : {});
  }
  return results;
}

function rollTypeFallback(types, stage, level, random, speciesName) {
  const results = [];
  const primary = DROP_BY_TYPE[types[0]];
  const budget = dropBudgetForLevel(level, stage);
  if (primary && random() < primaryDropChanceForLevel(level)) {
    const qty = budgetedQuantity(sellValue(primary), budget, random);
    if (qty > 0) pushDrop(results, primary.slug, qty);
  }
  const secondary = types[1] && DROP_BY_TYPE[types[1]];
  if (secondary && random() < 0.22) {
    const qty = budgetedQuantity(sellValue(secondary), budget * 0.42, random);
    if (qty > 0) pushDrop(results, secondary.slug, qty);
  }
  const signature = signatureDropForSpecies(types, speciesName);
  if (signature && random() < signatureDropChanceForStage(stage)) {
    const qty = budgetedQuantity(sellValue(signature), budget * (0.35 + Math.min(3, stage) * 0.08), random);
    if (qty > 0) pushDrop(results, signature.slug, qty, { signature: true });
  }
  const stones = STONES_BY_TYPE[types[0]];
  if (stones?.length && random() < stoneDropChanceForLevel(level)) {
    const stone = stones[Math.floor(random() * stones.length)];
    pushDrop(results, stone.slug, 1, { rare: true });
  }
  return results;
}

/** Tabela de loot da espécie (para Pokédex / auditoria). */
export function lootTableForSpecies(speciesName = '', types = []) {
  const slug = normalizeSpeciesSlug(speciesName);
  const table = speciesLootTable(slug);
  if (table?.length) return table.map((row) => ({ ...row }));
  // Fallback sintético espelhando rollTypeFallback
  const rows = [];
  const primary = DROP_BY_TYPE[types[0]];
  if (primary) rows.push({ slug: primary.slug, chance: Math.round(primaryDropChanceForLevel(50) * 100000), min: 1, max: 6, stone: false });
  const secondary = types[1] && DROP_BY_TYPE[types[1]];
  if (secondary) rows.push({ slug: secondary.slug, chance: 22000, min: 1, max: 3, stone: false });
  const signature = signatureDropForSpecies(types, speciesName);
  if (signature) rows.push({ slug: signature.slug, chance: Math.round(signatureDropChanceForStage(1) * 100000), min: 1, max: 2, stone: false });
  const stones = STONES_BY_TYPE[types[0]];
  if (stones?.[0]) rows.push({ slug: stones[0].slug, chance: Math.round(stoneDropChanceForLevel(50) * 100000), min: 1, max: 1, stone: true });
  return rows;
}

export function expectedStableLootValue(speciesName, types = [], level = 1, stage = 0) {
  const table = lootTableForSpecies(speciesName, types);
  const budget = dropBudgetForLevel(level, stage);
  const ev = stableExpectedValue(table, level);
  return ev * speciesLootQtyScale(ev, budget);
}

// Rola os drops de um pokémon derrotado (tabela por espécie, com fallback por tipo).
export function rollDrops(types, stage = 0, level = 1, random = Math.random, speciesName = '', zoneId = '') {
  const table = speciesLootTable(normalizeSpeciesSlug(speciesName));
  const results = table?.length
    ? rollSpeciesTable(table, level, stage, random)
    : rollTypeFallback(types || [], stage, level, random, speciesName);
  const endgameDrops = rollEndgameDrops(zoneId, level, random);
  for (const [slug, qty] of Object.entries(endgameDrops)) {
    pushDrop(results, slug, qty, { rare: true, dropOnly: true });
  }
  return results;
}

export const SHOP_ITEMS = [
  ...BALLS.filter((ball) => ball.slug !== 'masterball' && ball.slug !== 'apexball'),
  ...POTIONS,
  ...FISHING_ROD_ITEMS,
  ...SPECIAL_ITEMS.filter((item) => !item.dropOnly),
];
